<template>
  <div class="category-multiselect" ref="multiselect">
    <!-- Combined Box with Selected Categories and Dropdown Trigger -->
    <div
      class="category-box"
      :class="{ 'is-open': isOpen, 'is-disabled': disabled }"
      @click="toggleDropdown"
    >
      <!-- Selected Categories Display -->
      <div class="category-content">
        <div v-if="selectedCategories.length > 0" class="selected-categories">
          <div
            v-for="category in selectedCategories"
            :key="category.id"
            class="category-chip"
            @click.stop
          >
            <span>{{ category.name }}</span>
            <button
              type="button"
              @click.stop="removeCategory(category.id)"
              :disabled="disabled"
              class="remove-btn"
            >
              <font-awesome-icon :icon="['fas', 'times']" />
            </button>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="empty-state">
          {{ availableToAdd.length === 0 ? 'All categories selected' : 'Select categories...' }}
        </div>
      </div>

      <!-- Dropdown Caret -->
      <div class="dropdown-caret">
        <font-awesome-icon
          :icon="['fas', 'chevron-down']"
          :class="{ 'rotate': isOpen }"
        />
      </div>
    </div>

  <!-- Dropdown Menu -->
  <Transition name="dropdown">
    <div
      v-if="isOpen && availableToAdd.length > 0"
      class="dropdown-menu"
      :style="dropdownStyle"
    >
      <div
        v-for="category in availableToAdd"
        :key="category.id"
        class="dropdown-item"
        @click.stop="selectCategory(category.id)"
      >
      <span>{{ category.name }}</span>
        <span v-if="category.description" class="category-description ms-auto">
          {{ category.description }}
        </span>
      </div>
    </div>
  </Transition>
</div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps({
  modelValue: {
    type: Array, // Array of category IDs
    default: () => []
  },
  categories: {
    type: Array, // Array of category objects {id, name, description}
    default: () => []
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const multiselect = ref(null)
const isOpen = ref(false)
const dropdownStyle = ref({})

// Computed: Get selected category objects
const selectedCategories = computed(() => {
  return props.categories.filter(cat => props.modelValue.includes(cat.id))
})

// Computed: Get categories available to add (not yet selected)
const availableToAdd = computed(() => {
  return props.categories.filter(cat => !props.modelValue.includes(cat.id))
})

// Calculate dropdown position
const updateDropdownPosition = () => {
  if (!multiselect.value) return

  const rect = multiselect.value.querySelector('.category-box').getBoundingClientRect()
  dropdownStyle.value = {
    position: 'fixed',
    top: `${rect.bottom + 4}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`
  }
}

// Toggle dropdown
const toggleDropdown = () => {
  if (props.disabled || availableToAdd.value.length === 0) return

  isOpen.value = !isOpen.value
  if (isOpen.value) {
    updateDropdownPosition()
  }
}

// Select a category from dropdown
const selectCategory = (categoryId) => {
  if (!props.modelValue.includes(categoryId)) {
    const newValue = [...props.modelValue, categoryId]
    emit('update:modelValue', newValue)
  }
  isOpen.value = false // Close dropdown after selection
}

// Remove a category
const removeCategory = (categoryId) => {
  const newValue = props.modelValue.filter(id => id !== categoryId)
  emit('update:modelValue', newValue)
}

// Close dropdown when clicking outside
const handleClickOutside = (event) => {
  if (multiselect.value && !multiselect.value.contains(event.target)) {
    isOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.category-multiselect {
  width: 100%;
  position: relative;
}

/* Combined Category Box */
.category-box {
  background-color: #171C24;
  border: 1px solid #374151;
  border-radius: 10px;
  padding: 0.75rem 1rem;
  min-height: 48px;
  transition: border-color 0.2s;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.category-box:hover:not(.is-disabled) {
  border-color: #7357D2;
}

.category-box.is-open {
  border-color: #7357D2;
}

.category-box.is-disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

/* Category Content (left side) */
.category-content {
  flex: 1;
  min-width: 0;
}

/* Selected Categories Display */
.selected-categories {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.category-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  background-color: #7357D2;
  color: white;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
}

.category-chip .remove-btn {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.category-chip .remove-btn:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.2);
}

.category-chip .remove-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Empty State */
.empty-state {
  color: #6B7280;
  font-size: 0.875rem;
}

/* Dropdown Caret (right side) */
.dropdown-caret {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9CA3AF;
  font-size: 0.875rem;
  transition: transform 0.2s, color 0.2s;
  flex-shrink: 0;
}

.dropdown-caret svg.rotate {
  transform: rotate(180deg);
}

.category-box:hover:not(.is-disabled) .dropdown-caret {
  color: #7357D2;
}

/* Dropdown Menu */
.dropdown-menu {
  /* Position is set dynamically via style binding */
  background-color: #171C24;
  border: 1px solid #7357D2;
  border-radius: 10px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 9999;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.5), 0 4px 6px -2px rgba(0, 0, 0, 0.3);
  display: block;
}

/* Dropdown transition */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.dropdown-enter-to,
.dropdown-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.dropdown-item {
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
  color: white;
  font-size: 0.875rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover {
  background-color: rgba(115, 87, 210, 0.1);
  color: #a855f7;
}

.category-description {
  display: block;
  font-size: 0.75rem;
  color: #6B7280;
  margin-top: 0.25rem;
}

/* Custom scrollbar for dropdown */
.dropdown-menu::-webkit-scrollbar {
  width: 6px;
}

.dropdown-menu::-webkit-scrollbar-track {
  background: #1f2937;
  border-radius: 10px;
}

.dropdown-menu::-webkit-scrollbar-thumb {
  background: #374151;
  border-radius: 3px;
}

.dropdown-menu::-webkit-scrollbar-thumb:hover {
  background: #4b5563;
}
</style>
